import { useCallback, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';
import { UserGamification, UserSearchResponse, UserSearchType } from './types';

/**
 * User lookup for the fraud-flag / coin-adjust workflow. A single `q` search
 * covers all three forms — the backend resolves a user UUID, 4 digits (phone
 * last-4) or a display-name substring and reports which it used in
 * `search_type`. The full phone number is never returned.
 *
 * Name / phone searches return summaries; `getDetail` fetches the richer record
 * (flag history + activity series) for one user.
 */
export function useUserLookup(env: Env) {
  const { request } = useAdminApi();
  const [results, setResults] = useState<UserGamification[]>([]);
  const [searchType, setSearchType] = useState<UserSearchType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(
    async (term: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await request<UserSearchResponse>({
          env,
          path: '/admin/gamification/users',
          query: { q: term },
        });
        setResults(data.items);
        setSearchType(data.search_type ?? null);
        return data.items;
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Lookup failed';
        setError(msg);
        setResults([]);
        setSearchType(null);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [request, env],
  );

  /**
   * Every currently-flagged user. Not a search — there is no term to enter,
   * which is the point: a flagged user is otherwise only reachable by someone
   * who already knows to look them up.
   */
  const listFlagged = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await request<UserSearchResponse>({
        env,
        path: '/admin/gamification/users',
        query: { flagged: 'true' },
      });
      setResults(data.items);
      setSearchType(data.search_type ?? null);
      return data.items;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load flagged users');
      setResults([]);
      setSearchType(null);
      return [];
    } finally {
      setLoading(false);
    }
  }, [request, env]);

  /**
   * Detail for one user (flag history + activity). Does not replace the result
   * list — the panel merges it into the expanded card.
   */
  const getDetail = useCallback(
    async (userId: string): Promise<UserGamification | null> => {
      try {
        return await request<UserGamification>({
          env,
          path: `/admin/gamification/users/${userId}`,
        });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Could not load user detail');
        return null;
      }
    },
    [request, env],
  );

  return { results, searchType, loading, error, search, listFlagged, getDetail, setResults, setError };
}
